import React, { useState } from "react"

import { detect } from "detect-browser"
import { Form, Formik } from "formik"
import * as Yup from "yup"

import { AlertLevel, Color } from "~tailwindui/types/enums"

import { AlertMessageBox } from "~tailwindui/Basics"
import { SubmitButton, TextInput } from "~tailwindui/Form"
import Modal from "~tailwindui/Modal"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import { gql, useClient, useMutation } from "hooks/urql"

import { FormFailures, handleFailure } from "../Forms/Formik/hookComponents"

import StudentModal from "./StudentModal"

const ValidationSchema = Yup.object().shape({
  phoneNumber: Yup.string().required("Please specify a contact number"),
})

export type SupportModalProps = {
  phoneNumber: string
  sessionId: string
  error: { name: string; message: string }
  userType: "Student" | "Teacher"
  className?: string
}

const SupportModal: React.FC<SupportModalProps> = ({
  phoneNumber,
  sessionId,
  error,
  userType,
  className = undefined,
}) => {
  const browser = detect()
  const [successMessage, setSuccessMessage] = useState<string | null>()

  const client = useClient()
  const [, runMutation]: any[] = useMutation(supportMutation, client)

  const formState = {
    sessionId: sessionId,
    errorName: error?.name,
    errorMessage: error?.message,
    phoneNumber: phoneNumber,
    browserName: browser?.name,
    browserVersion: browser?.version,
    os: browser?.os,
  }

  const handleSubmit = (values, actions) => {
    runMutation({ input: values })
      .then(
        response => {
          const { failures } = response.data.supportRequest
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            setSuccessMessage(
              "A member of our support team will text you at the number you provided"
            )
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
      .finally(() => actions.setSubmitting(false))
  }

  return userType === "Student" ? (
    <StudentModal
      phoneNumber={phoneNumber}
      sessionId={sessionId}
      error={error}
      className={className}
    />
  ) : (
    <Modal.Dialog
      buttonText="Contact Support"
      buttonClassNames={buttonClassNames({ color: Color.Green })}
    >
      <Modal.Header>Support Request</Modal.Header>
      <Modal.Body>
        <Formik
          validationSchema={ValidationSchema}
          onSubmit={handleSubmit}
          initialValues={formState}
        >
          <Form>
            <div className="modal-body">
              <TextInput
                label="Contact Number"
                description="Number must be able to receive texts"
                name="phoneNumber"
                type="tel"
              />
            </div>
            <div className="flex-column flex">
              <FormFailures />
              {successMessage && (
                <AlertMessageBox level={AlertLevel.Success}>
                  {successMessage}
                </AlertMessageBox>
              )}
              {!successMessage && <SubmitButton text="Submit" />}
            </div>
          </Form>
        </Formik>
      </Modal.Body>
    </Modal.Dialog>
  )
}

const supportMutation = gql`
  mutation ($input: SupportRequestInputObject!) {
    supportRequest(input: $input) {
      failures {
        message
      }
    }
  }
`

export default SupportModal
